import {
	ClockCircleOutlined,
	DoubleLeftOutlined,
	DoubleRightOutlined,
	EditOutlined,
	LeftOutlined,
	QuestionCircleOutlined,
	RightOutlined,
	StopOutlined,
	UndoOutlined,
} from "@ant-design/icons";
import {
	Button,
	Collapse,
	DatePicker,
	Form,
	Input,
	InputNumber,
	Modal,
	message,
	Radio,
	Select,
	Tooltip,
} from "antd";
import dayjs from "dayjs";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import {
	useCreateOccurrences,
	useDeleteOccurrence,
	usePublicHolidays,
} from "../hooks";
import type {
	CreateOccurrencePayload,
	DayOfWeek,
	EventOccurrence,
} from "../types";
import { DAYS_OF_WEEK, SLOT_DURATION_OPTIONS } from "../types";
import {
	calculateWeekNavigation,
	createJSTISOString,
	generateTimeSlots,
	getDayHeaderColor,
	getDayOfWeekLabel,
	getJSTHours,
	getJSTMinutes,
	getMonthYearDisplay,
	getStartOfCurrentWeek,
	getWeekDates,
	isPastSlot,
	isSameDayJST,
	isToday,
	toJSTDayjs,
} from "../utils";
import { DayAdjustmentModal } from "./DayAdjustmentModal";

const getDayValue = (date: dayjs.Dayjs): number => {
	const day = date.day();
	return day === 0 ? 7 : day;
};

const { TextArea } = Input;
const { Option } = Select;

interface TimeSlotAdditionModalProps {
	open: boolean;
	onClose: () => void;
	eventOccasionId: number;
	existingOccurrences?: EventOccurrence[];
	primaryColor?: string;
	primaryLightColor?: string;
}

export const TimeSlotAdditionModal: React.FC<TimeSlotAdditionModalProps> = ({
	open,
	onClose,
	eventOccasionId,
	existingOccurrences = [],
	primaryColor = "#99CA29",
	primaryLightColor = "#fdfff0",
}) => {
	const [form] = Form.useForm();
	const [generateForm] = Form.useForm();

	const [slotDuration, setSlotDuration] = useState(60);
	const [reservableCount, setReservableCount] = useState(3);
	const [pendingOccurrences, setPendingOccurrences] = useState<
		Array<{
			maxAttendees: number;
			startAt: string;
			endAt: string;
		}>
	>([]);
	const [days, setDays] = useState<DayOfWeek[]>(
		DAYS_OF_WEEK.map((d) => ({ ...d })),
	);
	const [isDayAdjustmentOpen, setIsDayAdjustmentOpen] = useState(false);
	const [currentDayForAdjustment, setCurrentDayForAdjustment] = useState<
		DayOfWeek | undefined
	>();
	const [currentWeekStart, setCurrentWeekStart] = useState(
		getStartOfCurrentWeek,
	);

	const { data: publicHolidays = {} } = usePublicHolidays(open);
	const createMutation = useCreateOccurrences();
	const deleteMutation = useDeleteOccurrence();

	const weekDates = useMemo(
		() => getWeekDates(currentWeekStart),
		[currentWeekStart],
	);

	const timeRange = useMemo(() => {
		let minHour = 7;
		let maxHour = 21;

		if (existingOccurrences.length > 0) {
			for (const occ of existingOccurrences) {
				if (occ.startAt) {
					const startDate = new Date(occ.startAt);
					if (!Number.isNaN(startDate.getTime())) {
						minHour = Math.min(minHour, getJSTHours(occ.startAt));
					}
				}
				if (occ.endAt) {
					const endDate = new Date(occ.endAt);
					if (!Number.isNaN(endDate.getTime())) {
						const hour =
							getJSTHours(occ.endAt) + (getJSTMinutes(occ.endAt) > 0 ? 1 : 0);
						maxHour = Math.max(maxHour, hour);
					}
				}
			}
		}

		if (pendingOccurrences.length > 0) {
			for (const occ of pendingOccurrences) {
				if (occ.startAt) {
					const startDate = new Date(occ.startAt);
					if (!Number.isNaN(startDate.getTime())) {
						minHour = Math.min(minHour, getJSTHours(occ.startAt));
					}
				}
				if (occ.endAt) {
					const endDate = new Date(occ.endAt);
					if (!Number.isNaN(endDate.getTime())) {
						const hour =
							getJSTHours(occ.endAt) + (getJSTMinutes(occ.endAt) > 0 ? 1 : 0);
						maxHour = Math.max(maxHour, hour);
					}
				}
			}
		}

		return {
			startHour: Math.max(0, minHour),
			endHour: Math.min(24, maxHour),
		};
	}, [existingOccurrences, pendingOccurrences]);

	const timeSlots = useMemo(
		() =>
			generateTimeSlots(timeRange.startHour, timeRange.endHour, slotDuration),
		[timeRange.startHour, timeRange.endHour, slotDuration],
	);

	useEffect(() => {
		if (!open) {
			setPendingOccurrences([]);
			setSlotDuration(60);
			setReservableCount(3);
			setDays(DAYS_OF_WEEK.map((d) => ({ ...d })));
			form.resetFields();
			generateForm.resetFields();
		}
	}, [open, form, generateForm]);

	const navigateWeek = (
		direction: "prev" | "next" | "prevMonth" | "nextMonth",
	) => {
		setCurrentWeekStart(calculateWeekNavigation(currentWeekStart, direction));
	};

	const goToToday = () => {
		setCurrentWeekStart(getStartOfCurrentWeek());
	};

	const getExistingOccurrence = (
		date: Date,
		slotTime: string,
	): EventOccurrence | undefined => {
		const [slotHour, slotMinute] = slotTime.split(":").map(Number);
		const slotStartMinutes = slotHour * 60 + slotMinute;
		const slotEndMinutes = slotStartMinutes + slotDuration;

		return existingOccurrences.find((occ) => {
			if (!occ.startAt || !occ.endAt) return false;

			const occStartDate = new Date(occ.startAt);
			const occEndDate = new Date(occ.endAt);

			if (
				Number.isNaN(occStartDate.getTime()) ||
				Number.isNaN(occEndDate.getTime())
			) {
				return false;
			}

			if (!isSameDayJST(occ.startAt, date)) {
				return false;
			}

			const occStartMinutes =
				getJSTHours(occ.startAt) * 60 + getJSTMinutes(occ.startAt);
			const occEndMinutes =
				getJSTHours(occ.endAt) * 60 + getJSTMinutes(occ.endAt);

			return (
				occStartMinutes < slotEndMinutes && occEndMinutes > slotStartMinutes
			);
		});
	};

	const getPendingOccurrence = (
		date: Date,
		slotTime: string,
	): (typeof pendingOccurrences)[0] | undefined => {
		const [hour, minute] = slotTime.split(":").map(Number);
		return pendingOccurrences.find((occ) => {
			return (
				isSameDayJST(occ.startAt, date) &&
				getJSTHours(occ.startAt) === hour &&
				getJSTMinutes(occ.startAt) === minute
			);
		});
	};

	const handleSlotClick = (date: Date, slotTime: string) => {
		if (isPastSlot(date, slotTime)) return;

		const existingOcc = getExistingOccurrence(date, slotTime);
		const pendingOcc = getPendingOccurrence(date, slotTime);

		if (existingOcc) {
			if (existingOcc.sumExpected > 0) {
				message.warning("予約が入っている時間帯は削除できません");
				return;
			}
			Modal.confirm({
				title: "時間削除",
				icon: <QuestionCircleOutlined style={{ color: "#dc2626" }} />,
				content: (
					<div>
						<p>
							{toJSTDayjs(existingOcc.startAt).format("YYYY年M月D日 HH:mm")}
						</p>
						<p>この時間枠を削除しますか？</p>
					</div>
				),
				okText: "削除する",
				okButtonProps: { danger: true },
				cancelText: "閉じる",
				onOk: async () => {
					try {
						await deleteMutation.mutateAsync(existingOcc.occurrenceId);
						message.success("時間枠を削除しました");
					} catch {
						message.error("削除に失敗しました");
					}
				},
			});
			return;
		}

		if (pendingOcc) {
			setPendingOccurrences((prev) =>
				prev.filter((p) => p.startAt !== pendingOcc.startAt),
			);
			return;
		}

		const [hour, minute] = slotTime.split(":").map(Number);
		const startISO = createJSTISOString(date, hour, minute);
		const endMinutes = hour * 60 + minute + slotDuration;
		const endH = Math.floor(endMinutes / 60);
		const endM = endMinutes % 60;
		const endISO = createJSTISOString(date, endH, endM);

		setPendingOccurrences((prev) => [
			...prev,
			{
				maxAttendees: reservableCount,
				startAt: startISO,
				endAt: endISO,
			},
		]);
	};

	const clearPendingOccurrences = () => {
		setPendingOccurrences([]);
	};

	const handleGenerateOccurrence = (formData: {
		startDate: dayjs.Dayjs;
		endDate: dayjs.Dayjs;
		includeHoliday: string;
	}) => {
		const startDate = dayjs(formData.startDate);
		const endDate = dayjs(formData.endDate);

		if (!startDate.isValid() || !endDate.isValid()) {
			message.error("開始日と終了日を選択してください");
			return;
		}

		const tempOccurrences: typeof pendingOccurrences = [];
		let currentDate = startDate;

		while (
			currentDate.isBefore(endDate) ||
			currentDate.isSame(endDate, "day")
		) {
			const dayOfWeek = getDayValue(currentDate);
			const dayConfig = days.find((d) => d.value === dayOfWeek);

			if (dayConfig?.enabled && dayConfig.times && dayConfig.times.length > 0) {
				const dateStr = currentDate.format("YYYY-MM-DD");
				const isHoliday = publicHolidays[dateStr];

				if (formData.includeHoliday === "1" || !isHoliday) {
					dayConfig.times.forEach((timeSlot) => {
						if (timeSlot.startTime && timeSlot.endTime) {
							const startTimeObj = dayjs(timeSlot.startTime);
							const endTimeObj = dayjs(timeSlot.endTime);

							if (startTimeObj.isValid() && endTimeObj.isValid()) {
								let curH = startTimeObj.hour();
								let curM = startTimeObj.minute();
								const endTotalMinutes =
									endTimeObj.hour() * 60 + endTimeObj.minute();
								const calDate = currentDate.toDate();

								while (curH * 60 + curM < endTotalMinutes) {
									const slotEndMinutes = curH * 60 + curM + slotDuration;

									if (slotEndMinutes <= endTotalMinutes) {
										const jstStartISO = createJSTISOString(calDate, curH, curM);
										const jstEndH = Math.floor(slotEndMinutes / 60);
										const jstEndM = slotEndMinutes % 60;
										const jstEndISO = createJSTISOString(
											calDate,
											jstEndH,
											jstEndM,
										);

										const alreadyExists = pendingOccurrences.some(
											(p) => p.startAt === jstStartISO,
										);

										const existsInExisting = existingOccurrences.some((e) =>
											dayjs(e.startAt).isSame(dayjs(jstStartISO)),
										);

										if (!alreadyExists && !existsInExisting) {
											tempOccurrences.push({
												maxAttendees: reservableCount,
												startAt: jstStartISO,
												endAt: jstEndISO,
											});
										}
									}

									const nextMinutes = curH * 60 + curM + slotDuration;
									curH = Math.floor(nextMinutes / 60);
									curM = nextMinutes % 60;
								}
							}
						}
					});
				}
			}

			currentDate = currentDate.add(1, "day");
		}

		if (tempOccurrences.length > 0) {
			setPendingOccurrences([...pendingOccurrences, ...tempOccurrences]);
			message.success(`${tempOccurrences.length}件の時間枠を作成しました`);
		} else {
			message.warning(
				"生成する時間枠がありません。日付範囲と曜日設定を確認してください。",
			);
		}
	};

	const handleSave = async () => {
		if (pendingOccurrences.length === 0) {
			message.warning("追加する時間枠を選択してください");
			return;
		}

		try {
			const payload: CreateOccurrencePayload = {
				eventOccasionId,
				templateSlots: pendingOccurrences,
			};
			await createMutation.mutateAsync(payload);
			message.success("時間枠を追加しました");
			onClose();
		} catch {
			message.error("時間枠の追加に失敗しました");
		}
	};

	return (
		<Modal
			open={open}
			onCancel={onClose}
			title="時間追加"
			footer={null}
			width={720}
			styles={{
				body: {
					maxHeight: "90vh",
					overflowY: "auto",
					overflowX: "hidden",
				},
			}}
			maskClosable={false}
			destroyOnClose
			centered
		>
			<div style={{ padding: 8 }}>
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						marginBottom: 16,
					}}
				>
					<div>
						<p style={{ marginBottom: 8 }}>既定予約可能最大人数</p>
						<InputNumber
							min={0}
							value={reservableCount}
							onChange={(val) => setReservableCount(val || 0)}
							style={{ width: 100 }}
						/>
					</div>
					<div>
						<p style={{ marginBottom: 8 }}>1枠の時間</p>
						<Select
							value={slotDuration}
							onChange={(val) => {
								setSlotDuration(val);
								clearPendingOccurrences();
							}}
							style={{ width: 120 }}
						>
							{SLOT_DURATION_OPTIONS.map((opt) => (
								<Option key={opt.value} value={opt.value}>
									{opt.label}
								</Option>
							))}
						</Select>
					</div>
				</div>

				<Collapse
					style={{ marginBottom: 16 }}
					items={[
						{
							key: "detail",
							label: "詳細設定",
							children: (
								<div>
									<p style={{ textAlign: "center", fontWeight: "bold" }}>
										予約可能スケジュールを表示する期間を設定することができます。
									</p>
									<p style={{ textAlign: "center", marginBottom: 16 }}>
										時間帯ごとの予約可能最大人数と1枠の時間は上記の設定を利用します。
									</p>
									<Form
										form={generateForm}
										layout="vertical"
										size="large"
										requiredMark={false}
										onFinish={handleGenerateOccurrence}
									>
										<Form.Item
											label="期間の設定をする"
											required
											style={{ marginBottom: 0 }}
										>
											<div style={{ display: "flex", gap: 8 }}>
												<Form.Item
													name="startDate"
													style={{ width: "calc(50% - 4px)" }}
													extra="※開始日"
													rules={[{ required: true, message: "必須です" }]}
												>
													<DatePicker
														style={{ width: "100%" }}
														disabledDate={(current) =>
															current && current < dayjs().startOf("day")
														}
													/>
												</Form.Item>
												<Form.Item
													name="endDate"
													style={{ width: "calc(50% - 4px)" }}
													extra="※終了日"
													rules={[{ required: true, message: "必須です" }]}
												>
													<DatePicker
														style={{ width: "100%" }}
														disabledDate={(current) =>
															current && current < dayjs().startOf("day")
														}
													/>
												</Form.Item>
											</div>
										</Form.Item>
										<div
											style={{
												display: "flex",
												flexWrap: "wrap",
												justifyContent: "center",
												gap: 8,
												marginBottom: 16,
											}}
										>
											{days.map((day) => (
												<button
													type="button"
													onClick={() => {
														setCurrentDayForAdjustment(day);
														setIsDayAdjustmentOpen(true);
													}}
													key={day.value}
													style={{
														display: "flex",
														flexDirection: "column",
														border: day.enabled
															? `1px solid ${primaryColor}`
															: "1px solid #d9d9d9",
														borderRadius: 4,
														padding: "8px 4px",
														backgroundColor: day.enabled
															? primaryLightColor
															: "white",
														cursor: "pointer",
														minWidth: 75,
													}}
												>
													<p
														style={{
															fontWeight: "bold",
															textAlign: "center",
															color:
																day.value === 6
																	? "#2563eb"
																	: day.value === 7
																		? "#dc2626"
																		: "#374151",
														}}
													>
														{day.longLabel}
													</p>
													<div
														style={{
															display: "flex",
															flexDirection: "column",
															marginTop: 8,
														}}
													>
														{day.times && day.times.length > 0
															? day.times.map((time, idx) => (
																	<p
																		key={idx}
																		style={{
																			fontSize: 10,
																			textAlign: "center",
																		}}
																	>
																		{time?.startTime
																			? dayjs(time.startTime).format("HH:mm")
																			: ""}
																		~
																		{time?.endTime
																			? dayjs(time.endTime).format("HH:mm")
																			: ""}
																	</p>
																))
															: null}
													</div>
													<div
														style={{
															display: "flex",
															justifyContent: "center",
														}}
													>
														<EditOutlined
															style={{ fontSize: 12, marginTop: 4 }}
														/>
													</div>
												</button>
											))}
										</div>
										<Form.Item
											name="includeHoliday"
											label="祝日は予約を受け付けますか？"
										>
											<Radio.Group>
												<Radio value="1">受け付ける</Radio>
												<Radio value="0">受け付けない</Radio>
											</Radio.Group>
										</Form.Item>
										<Form.Item name="remarks" label="備考欄">
											<TextArea
												placeholder="上記の時間に対しての備考欄"
												autoSize
											/>
										</Form.Item>
										<div style={{ display: "flex", justifyContent: "center" }}>
											<Button
												type="primary"
												htmlType="submit"
												style={{
													backgroundColor: primaryColor,
													borderColor: primaryColor,
												}}
											>
												自動作成
											</Button>
										</div>
										<p style={{ textAlign: "center", marginTop: 16 }}>
											※時間帯を実際に作られる為には保存する必要があります
										</p>
									</Form>
								</div>
							),
						},
					]}
				/>

				<div
					style={{
						display: "flex",
						justifyContent: "end",
						marginBottom: 16,
						gap: 4,
					}}
				>
					<Button
						icon={<DoubleLeftOutlined />}
						onClick={() => navigateWeek("prevMonth")}
					/>
					<Button
						icon={<LeftOutlined />}
						onClick={() => navigateWeek("prev")}
					/>
					<Button onClick={goToToday}>今週</Button>
					<Button
						icon={<RightOutlined />}
						onClick={() => navigateWeek("next")}
					/>
					<Button
						icon={<DoubleRightOutlined />}
						onClick={() => navigateWeek("nextMonth")}
					/>
				</div>

				<div
					style={{
						textAlign: "center",
						fontSize: 20,
						fontWeight: "bold",
						marginBottom: 16,
					}}
				>
					{getMonthYearDisplay(weekDates)}
				</div>

				<div
					style={{
						display: "grid",
						gridTemplateColumns: "60px repeat(7, 1fr)",
						border: "1px solid #e5e7eb",
						borderRadius: 4,
						overflow: "hidden",
					}}
				>
					<div
						style={{
							backgroundColor: "#f9fafb",
							borderBottom: "1px solid #e5e7eb",
							borderRight: "1px solid #e5e7eb",
							padding: 8,
						}}
					/>
					{weekDates.map((date) => (
						<div
							key={date.toISOString()}
							style={{
								backgroundColor: "#f9fafb",
								borderBottom: "1px solid #e5e7eb",
								borderRight: "1px solid #e5e7eb",
								padding: 8,
								textAlign: "center",
								fontWeight: "bold",
								color: getDayHeaderColor(date),
							}}
						>
							{date.getDate()} ({getDayOfWeekLabel(date)})
						</div>
					))}

					{timeSlots.flatMap((slot) => [
						<div
							key={slot}
							style={{
								backgroundColor: "#f9fafb",
								borderBottom: "1px solid #e5e7eb",
								borderRight: "1px solid #e5e7eb",
								padding: "12px 8px",
								fontSize: 12,
								textAlign: "right",
							}}
						>
							{slot}
						</div>,
						...weekDates.map((date) => {
							const existingOcc = getExistingOccurrence(date, slot);
							const pendingOcc = getPendingOccurrence(date, slot);
							const isPast = isPastSlot(date, slot);
							const isTodayDate = isToday(date);

							let backgroundColor = "white";
							if (isTodayDate) backgroundColor = primaryLightColor;
							if (isPast) backgroundColor = "rgba(195, 195, 195, 0.15)";
							if (pendingOcc) backgroundColor = primaryLightColor;

							const slotStyle = {
								borderBottom: "1px solid #e5e7eb",
								borderRight: "1px solid #e5e7eb",
								padding: 4,
								minHeight: 36,
								backgroundColor,
								cursor: isPast ? "default" : "pointer",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							};

							return !isPast ? (
								<button
									type="button"
									key={`${date.toISOString()}-${slot}`}
									style={slotStyle}
									onClick={() => handleSlotClick(date, slot)}
								>
									{existingOcc && (
										<Tooltip
											title={
												existingOcc.sumExpected > 0
													? `予約が${existingOcc.sumExpected}件あります`
													: "クリックで削除"
											}
										>
											{existingOcc.sumExpected > 0 ? (
												<StopOutlined style={{ color: "#f59e0b" }} />
											) : (
												<ClockCircleOutlined style={{ color: primaryColor }} />
											)}
										</Tooltip>
									)}
									{pendingOcc && !existingOcc && (
										<ClockCircleOutlined style={{ color: primaryColor }} />
									)}
								</button>
							) : (
								<div key={`${date.toISOString()}-${slot}`} style={slotStyle}>
									{existingOcc && (
										<Tooltip
											title={
												existingOcc.sumExpected > 0
													? `予約が${existingOcc.sumExpected}件あります`
													: "クリックで削除"
											}
										>
											{existingOcc.sumExpected > 0 ? (
												<StopOutlined style={{ color: "#f59e0b" }} />
											) : (
												<ClockCircleOutlined style={{ color: primaryColor }} />
											)}
										</Tooltip>
									)}
									{pendingOcc && !existingOcc && (
										<ClockCircleOutlined style={{ color: primaryColor }} />
									)}
								</div>
							);
						}),
					])}
				</div>

				<div
					style={{ display: "flex", justifyContent: "center", marginTop: 16 }}
				>
					<Button
						danger
						icon={<UndoOutlined />}
						onClick={clearPendingOccurrences}
					>
						やり直し
					</Button>
				</div>

				<div
					style={{
						display: "flex",
						justifyContent: "center",
						marginTop: 24,
						gap: 8,
					}}
				>
					<Button
						type="primary"
						size="large"
						onClick={handleSave}
						loading={createMutation.isPending}
						disabled={pendingOccurrences.length === 0}
						style={{
							width: 120,
							backgroundColor: primaryColor,
							borderColor: primaryColor,
						}}
					>
						保存する
					</Button>
				</div>
			</div>

			<DayAdjustmentModal
				open={isDayAdjustmentOpen}
				onClose={() => {
					setIsDayAdjustmentOpen(false);
					setCurrentDayForAdjustment(undefined);
				}}
				currentDay={currentDayForAdjustment}
				days={days}
				onSave={(updatedDays) => setDays(updatedDays)}
				primaryColor={primaryColor}
				primaryLightColor={primaryLightColor}
			/>
		</Modal>
	);
};
